import {
  Component,
  ChangeDetectionStrategy,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription, finalize } from 'rxjs';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DrawerModule } from 'primeng/drawer';
import { DrawerResizeDirective } from '../directives/drawer-resize.directive';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { MessageService } from 'primeng/api';
import {
  AddRecruiterPayload,
  Recruiter,
  RecruiterRole,
  TeamService,
} from './team.service';

/** Whether the shared form is inviting a new recruiter or editing an
 *  existing one. Derived from whether the host passed a member to edit;
 *  drives the form heading, submit label, and which service call
 *  `submit()` dispatches. */
type FormMode = 'add' | 'edit';

/**
 * Add / edit form for a single recruiter, presented as a side drawer.
 * The roster itself lives on the Team settings page (TeamPage) — this
 * drawer is the focused single-member intake surface it launches for
 * "Add recruiter" and per-row "Edit".
 *
 * Contract (all declarative, parent-owned):
 *   - [visible] / (visibleChange): two-way open state.
 *   - [editMember]: the member to edit, or null for add mode. Read on
 *     open, so the parent sets it alongside flipping [visible] true.
 *   - [roster]: the current roster, used only for the duplicate-email
 *     check — the drawer never mutates or renders it as a list.
 *   - (recruiterAdded) / (recruiterUpdated): fire on success so the
 *     page can fold the change into its own list without a reload.
 *
 * Design intent: drawer over dialog to match the app's other intake
 * flows (Add applicant, backfill drawer, jobs form drawer), keeping the
 * vernacular consistent.
 *
 * Backend note: TeamService is stubbed against an in-memory roster until
 * kaboom-tenant-management exposes the recruiter endpoints; this
 * component talks only to that service, so it needs no change when the
 * real endpoints land.
 */
@Component({
  selector: 'lib-manage-team-drawer',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DrawerModule,
    DrawerResizeDirective,
    SelectModule,
    InputTextModule,
    ButtonModule,
  ],
  templateUrl: './manage-team-drawer.html',
  styleUrl: './manage-team-drawer.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ManageTeamDrawer {
  private readonly messageService = inject(MessageService);
  private readonly svc            = inject(TeamService);
  private readonly destroyRef     = inject(DestroyRef);

  /** Two-way visibility binding — parent owns state, drawer forwards
   *  dismiss events up so parent always has the last word. */
  readonly visible       = input.required<boolean>();
  readonly visibleChange = output<boolean>();

  /** The member to edit, or null for add mode. Read on open (see the
   *  effect below); changing it while the drawer is already open does
   *  not re-prime the form, so in-flight edits are never clobbered. */
  readonly editMember = input<Recruiter | null>(null);

  /** The member captured for this drawer session, snapshotted from
   *  editMember by prime() on open. Everything that decides what a save
   *  targets — mode, the duplicate-email exclusion, submit() — reads this
   *  rather than the live input, so a consumer mutating editMember while
   *  the drawer is open can't retarget an in-flight edit onto a different
   *  record. Null means add mode. */
  private readonly sessionMember = signal<Recruiter | null>(null);

  /** Current roster, supplied by the page. Used only to flag a
   *  duplicate email — never rendered here. */
  readonly roster = input<Recruiter[]>([]);

  /** Fired after a successful add / update so the parent can fold the
   *  change into the roster it renders (server-assigned id included). */
  readonly recruiterAdded   = output<Recruiter>();
  readonly recruiterUpdated = output<Recruiter>();

  // ── Form state (shared between add and edit) ────────────────────────────────
  readonly firstName = signal<string>('');
  readonly lastName  = signal<string>('');
  readonly email     = signal<string>('');
  readonly role      = signal<RecruiterRole>('Recruiter');

  /** Add vs edit is purely a function of whether a member was captured
   *  for this session. */
  readonly mode = computed<FormMode>(() => (this.sessionMember() ? 'edit' : 'add'));

  /** In-flight add/edit request. Tracked so a mid-request dismiss or
   *  duplicate click doesn't leak or land a stale callback. */
  private saveSubscription?: Subscription;
  readonly isSaving = signal(false);

  readonly roleOptions: { label: string; value: RecruiterRole }[] = [
    { label: 'Admin',     value: 'Admin' },
    { label: 'Recruiter', value: 'Recruiter' },
    { label: 'Viewer',    value: 'Viewer' },
  ];

  constructor() {
    // Prime the form on open, reset on close. Keyed on the visibility
    // transition (not every re-render) so re-priming can't clobber
    // in-progress edits; editMember is read untracked so a parent
    // updating it mid-edit doesn't retrigger this either.
    let wasVisible = false;
    effect(() => {
      const visible = this.visible();
      if (visible && !wasVisible) this.prime();
      else if (!visible && wasVisible) this.reset();
      wasVisible = visible;
    });
  }

  /** Lightweight local@domain.tld shape check. Empty is invalid because
   *  email is required for a recruiter (login identity). */
  private static readonly EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  readonly emailInvalid = computed(() => {
    const value = this.email();
    // Empty is not "malformed" (blocker list surfaces the missing case
    // as its own reason); only true when non-empty and non-conforming.
    return value.length > 0 && !ManageTeamDrawer.EMAIL_PATTERN.test(value);
  });

  /** True when the entered email already belongs to another roster member
   *  (case-insensitive). The member being edited is excluded so re-saving
   *  an unchanged email isn't flagged against itself. */
  readonly duplicateEmail = computed(() => {
    const value = this.email().trim().toLowerCase();
    if (value.length === 0) return false;
    const editingId = this.sessionMember()?.id ?? null;
    return this.roster().some(
      r => r.id !== editingId && r.email.toLowerCase() === value,
    );
  });

  /** All the reasons the submit button is currently blocked, surfaced
   *  together so the recruiter sees every fix in one pass. */
  readonly blockers = computed<string[]>(() => {
    const reasons: string[] = [];
    if (this.firstName().trim().length === 0) reasons.push('Enter a first name.');
    if (this.lastName().trim().length === 0)  reasons.push('Enter a last name.');
    if (this.email().length === 0)            reasons.push('Enter an email address.');
    else if (this.emailInvalid())             reasons.push('Fix the email address.');
    else if (this.duplicateEmail())           reasons.push('That email is already on your team.');
    return reasons;
  });

  readonly canSave = computed(() => this.blockers().length === 0);

  onVisibleChange(visible: boolean): void {
    // Forward the change upward; reset() is owned by the visibility
    // effect (constructor) so it runs exactly once per transition —
    // calling it here as well would double-fire when the emit round-
    // trips through the parent, cancels the in-flight save twice, and
    // clears field values twice on the same tick.
    this.visibleChange.emit(visible);
  }

  /** Ask the parent to close the drawer; the reset runs off the
   *  resulting visibility transition. */
  private close(): void {
    this.visibleChange.emit(false);
  }

  /** Dispatch the current form to add or update depending on mode. */
  submit(): void {
    if (!this.canSave() || this.isSaving()) return;

    const payload: AddRecruiterPayload = {
      firstName: this.firstName().trim(),
      lastName:  this.lastName().trim(),
      email:     this.email().trim(),
      role:      this.role(),
    };

    const member = this.sessionMember();
    if (member) this.saveEdit(member.id, payload);
    else this.saveAdd(payload);
  }

  private saveAdd(payload: AddRecruiterPayload): void {
    this.saveSubscription?.unsubscribe();
    this.isSaving.set(true);
    this.saveSubscription = this.svc.addRecruiter(payload)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => { this.isSaving.set(false); this.saveSubscription = undefined; }),
      )
      .subscribe({
        next: created => {
          this.messageService.add({
            severity: 'success',
            summary:  'Recruiter added',
            detail:   `${created.firstName} ${created.lastName} has been invited.`,
          });
          this.recruiterAdded.emit(created);
          // Clear isSaving BEFORE close() — reset() reads it to decide
          // whether to emit the 'Save cancelled' toast, and the effect
          // may flush before finalize runs. Setting it here is
          // authoritative; finalize's own set(false) becomes a no-op.
          this.isSaving.set(false);
          this.close();
        },
        error: () => this.messageService.add({
          severity: 'error',
          summary:  'Add failed',
          detail:   'Could not add the recruiter. Please try again.',
        }),
      });
  }

  private saveEdit(id: string, payload: AddRecruiterPayload): void {
    this.saveSubscription?.unsubscribe();
    this.isSaving.set(true);
    this.saveSubscription = this.svc.updateRecruiter(id, payload)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => { this.isSaving.set(false); this.saveSubscription = undefined; }),
      )
      .subscribe({
        next: updated => {
          this.messageService.add({
            severity: 'success',
            summary:  'Changes saved',
            detail:   `Updated ${updated.firstName} ${updated.lastName}.`,
          });
          this.recruiterUpdated.emit(updated);
          // Clear isSaving BEFORE close() — see saveAdd for the rationale.
          this.isSaving.set(false);
          this.close();
        },
        error: () => this.messageService.add({
          severity: 'error',
          summary:  'Save failed',
          detail:   'Could not save the changes. Please try again.',
        }),
      });
  }

  /** Populate the form for the member being edited, or clear it for a
   *  fresh add. Reads editMember untracked so it snapshots the value at
   *  open time only. */
  private prime(): void {
    const member = untracked(this.editMember);
    this.sessionMember.set(member);
    if (member) {
      this.firstName.set(member.firstName);
      this.lastName.set(member.lastName);
      this.email.set(member.email);
      this.role.set(member.role);
    } else {
      this.resetForm();
    }
  }

  /** Clear the form fields back to defaults. */
  private resetForm(): void {
    this.firstName.set('');
    this.lastName.set('');
    this.email.set('');
    this.role.set('Recruiter');
  }

  /** Full reset on drawer dismissal — cancel any in-flight save and drop
   *  back to a clean form. Silent cancellation would leave the recruiter
   *  wondering why their save 'took' but nothing appeared, so we surface
   *  the abort via a toast when there was actually work in flight. */
  private reset(): void {
    const wasSaving = this.isSaving();
    this.saveSubscription?.unsubscribe();
    this.saveSubscription = undefined;
    this.isSaving.set(false);
    this.sessionMember.set(null);
    this.resetForm();
    if (wasSaving) {
      this.messageService.add({
        severity: 'info',
        summary:  'Save cancelled',
        detail:   'The drawer was closed before the change could be saved.',
      });
    }
  }
}
